import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Mic, Camera, FileText, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AudioRecorder } from "@/components/smartfile/AudioRecorder";
import { CameraCapture } from "@/components/smartfile/CameraCapture";
import { TextInput } from "@/components/smartfile/TextInput";

interface MultimodalCaptureProps {
  patientId: string;
  onItemsExtracted: (items: any[]) => void;
}

type CaptureMode = "audio" | "photo" | "text" | null;

export function MultimodalCapture({ patientId, onItemsExtracted }: MultimodalCaptureProps) {
  const [mode, setMode] = useState<CaptureMode>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const processCapture = async (type: string, data: string, patientContext?: string) => {
    setIsProcessing(true);
    try {
      const { data: result, error } = await supabase.functions.invoke("process-prescription-input", {
        body: {
          type,
          data,
          patientContext,
        },
      });

      if (error) {
        if (error.message?.includes("429")) {
          toast.error("Limite de requisições excedido. Aguarde um momento e tente novamente.");
        } else if (error.message?.includes("402")) {
          toast.error("Créditos insuficientes. Adicione créditos em Configurações → Workspace → Uso.");
        } else {
          toast.error("Erro ao processar entrada: " + error.message);
        }
        return;
      }

      if (result && result.items && result.items.length > 0) {
        onItemsExtracted(result);
        setMode(null);
        toast.success(`${result.items.length} item(ns) extraído(s) com sucesso!`);
      } else if (result && result.patient_info) {
        // Paciente identificado mas sem itens
        onItemsExtracted(result);
        setMode(null);
        toast.info("Paciente identificado, mas nenhum medicamento foi encontrado.");
      } else {
        toast.warning("Nenhum item de prescrição foi identificado. Tente gravar uma prescrição médica clara com nome do medicamento, dosagem e posologia.");
      }
    } catch (error) {
      console.error("Error processing capture:", error);
      toast.error("Erro ao processar entrada");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAudioComplete = async (audioFile: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = (reader.result as string).split(",")[1];
      processCapture("audio", base64);
    };
    reader.readAsDataURL(audioFile);
  };

  const handlePhotoCapture = async (photoFile: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = (reader.result as string).split(",")[1];
      processCapture("photo", base64);
    };
    reader.readAsDataURL(photoFile);
  };

  const handleTextSubmit = async (text: string) => {
    processCapture("text", text);
  };

  return (
    <>
      <Card className="border-teal-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-teal-700 dark:text-teal-300">
            Captura Multimodal
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Button
              variant="outline"
              className="h-24 flex flex-col gap-2 border-teal-300 hover:bg-teal-50 dark:hover:bg-teal-950/30"
              onClick={() => setMode("audio")}
              disabled={isProcessing}
            >
              <Mic className="h-8 w-8 text-teal-600" />
              <span>Gravar Áudio</span>
            </Button>

            <Button
              variant="outline"
              className="h-24 flex flex-col gap-2 border-cyan-300 hover:bg-cyan-50 dark:hover:bg-cyan-950/30"
              onClick={() => setMode("photo")}
              disabled={isProcessing}
            >
              <Camera className="h-8 w-8 text-cyan-600" />
              <span>Capturar Foto</span>
            </Button>

            <Button
              variant="outline"
              className="h-24 flex flex-col gap-2 border-teal-300 hover:bg-teal-50 dark:hover:bg-teal-950/30"
              onClick={() => setMode("text")}
              disabled={isProcessing}
            >
              <FileText className="h-8 w-8 text-teal-600" />
              <span>Digitar Texto</span>
            </Button>
          </div>

          {isProcessing && (
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Processando com IA...
            </div>
          )}
        </CardContent>
      </Card>

      {mode === "photo" && (
        <CameraCapture
          onCapture={handlePhotoCapture}
          onCancel={() => setMode(null)}
        />
      )}

      <Dialog open={mode === "audio"} onOpenChange={(open) => !open && setMode(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gravar Prescrição por Áudio</DialogTitle>
          </DialogHeader>
          <AudioRecorder
            onRecordingComplete={handleAudioComplete}
            onCancel={() => setMode(null)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={mode === "text"} onOpenChange={(open) => !open && setMode(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Digitar Prescrição</DialogTitle>
          </DialogHeader>
          <TextInput
            onSubmit={handleTextSubmit}
            onCancel={() => setMode(null)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
